import React from 'react';
import {
  Box,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Text,
  List,
  ListItem,
  ListIcon,
  Code,
  Divider,
  Alert,
  AlertIcon,
  SimpleGrid,
  Card,
  CardBody,
  Heading,
  VStack,
  HStack,
  Badge,
  Link,
  Button,
  Image,
  Flex,
} from '@chakra-ui/react';
import {
  FaInfoCircle,
  FaLightbulb,
  FaTools,
  FaUserCheck,
  FaChartLine,
  FaNewspaper,
  FaCheckCircle,
  FaSearch,
  FaBuilding,
  FaShieldAlt,
  FaExchangeAlt,
  FaDownload,
  FaStar,
  FaProjectDiagram,
  FaBook,
  FaKey,
  FaRobot,
  FaChartBar,
  FaDatabase,
  FaCode,
  FaBrain,
  FaHistory,
  FaFilter,
  FaTable,
  FaFileExport,
  FaGlobe,
  FaRocket,
  FaCog,
  FaChartPie,
  FaRss,
} from 'react-icons/fa';

function HelpSection() {
  return (
    <Box>
      <Alert status="info" mb={6} borderRadius="md">
        <AlertIcon />
        Welcome to the Neatlabs™ Cyber & Digital Intelligence platform! This comprehensive guide will help you make the most of our platform's features.
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mb={8}>
        <Card>
          <CardBody>
            <Heading size="sm" mb={3} display="flex" alignItems="center" gap={2}>
              <FaInfoCircle />
              Platform Overview
            </Heading>
            <Text>
              An AI-powered platform for exploring cybersecurity, digital risk, privacy, and intelligence services. Analyze vendors, track industry news, and map security frameworks across the complete digital threat landscape.
            </Text>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Heading size="sm" mb={3} display="flex" alignItems="center" gap={2}>
              <FaLightbulb />
              Key Features
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={FaCheckCircle} />
                AI-Powered Company Analysis
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} />
                Real-time News Analysis
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} />
                Security Framework Mappings
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} />
                Market Analytics Dashboard
              </ListItem>
            </List>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Heading size="sm" mb={3} display="flex" alignItems="center" gap={2}>
              <FaKey />
              Getting Started
            </Heading>
            <Text>
              Enter your OpenAI API key to unlock AI-powered analysis features. Your key is securely stored in your browser's local storage.
            </Text>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Accordion allowMultiple>
        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" display="flex" alignItems="center" gap={2}>
                <FaShieldAlt />
                Company Analysis Features
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Heading size="sm" mb={2}>Company Search & Filtering</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaSearch} />
                    Search across all companies and categories
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaFilter} />
                    Filter by security categories
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaStar} />
                    Favorite companies for quick access
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Detailed Company Analysis</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaBrain} />
                    AI-generated company overviews
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaTools} />
                    Core products and services
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaChartLine} />
                    Market position analysis
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaRocket} />
                    Innovation tracking
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCog} />
                    SWOT analysis
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Company Comparison</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaExchangeAlt} />
                    Side-by-side company comparisons
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaTable} />
                    Comparative metrics
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaProjectDiagram} />
                    Competitive analysis
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Export & Share</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaFileExport} />
                    Export detailed analysis reports
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaGlobe} />
                    Direct website links
                  </ListItem>
                </List>
              </Box>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" display="flex" alignItems="center" gap={2}>
                <FaChartLine />
                Analytics Dashboard
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Heading size="sm" mb={2}>Market Overview</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaChartBar} />
                    Category distribution analysis
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaChartPie} />
                    Market focus distribution
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaDatabase} />
                    Company metrics and statistics
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Technology Trends</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaRocket} />
                    Emerging technology adoption
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaChartLine} />
                    Growth analysis
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaProjectDiagram} />
                    Market segment analysis
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Interactive Visualizations</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaChartBar} />
                    Dynamic charts and graphs
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaTable} />
                    Detailed data tables
                  </ListItem>
                </List>
              </Box>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" display="flex" alignItems="center" gap={2}>
                <FaNewspaper />
                News Analysis
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Heading size="sm" mb={2}>Real-time News Feed</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaRss} />
                    Live cybersecurity news updates
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaBrain} />
                    AI-powered news analysis
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaHistory} />
                    Historical news archive
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Analysis Components</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} />
                    Executive summaries
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaTools} />
                    Technical impact assessment
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaChartLine} />
                    Industry implications
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaShieldAlt} />
                    Security recommendations
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Solution Mapping</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaProjectDiagram} />
                    Relevant vendor recommendations
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCode} />
                    Technical mitigation steps
                  </ListItem>
                </List>
              </Box>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" display="flex" alignItems="center" gap={2}>
                <FaBook />
                Framework Mappings
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Heading size="sm" mb={2}>Supported Frameworks</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaShieldAlt} />
                    NIST SP 800-53 Rev. 5
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaShieldAlt} />
                    NIST Cybersecurity Framework (CSF)
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaShieldAlt} />
                    MITRE ATT&CK Framework
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Mapping Features</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaProjectDiagram} />
                    Control family mappings
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaBuilding} />
                    Solution category alignment
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaSearch} />
                    Gap analysis
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Implementation Guidance</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaTools} />
                    Control implementation suggestions
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaCheckCircle} />
                    Compliance mapping
                  </ListItem>
                </List>
              </Box>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" display="flex" alignItems="center" gap={2}>
                <FaLightbulb />
                Pro Tips & Best Practices
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Heading size="sm" mb={2}>Efficient Navigation</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaStar} />
                    Use favorites for quick access to frequently viewed companies
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaSearch} />
                    Utilize advanced search filters
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaHistory} />
                    Access cached analyses for faster viewing
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>Analysis Workflow</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaExchangeAlt} />
                    Compare similar companies for better insights
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaFileExport} />
                    Export reports for offline analysis
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaProjectDiagram} />
                    Use framework mappings for compliance planning
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>News Analysis</Heading>
                <List spacing={2}>
                  <ListItem>
                    <ListIcon as={FaNewspaper} />
                    Check news analysis for vendor recommendations
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FaDownload} />
                    Save important analyses for future reference
                  </ListItem>
                </List>
              </Box>
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <Box mt={8} p={6} bg="gray.50" borderRadius="lg">
        <Heading size="md" mb={4}>Need More Help?</Heading>
        <Text mb={4}>
          For additional support or feature requests, please reach out to our team:
        </Text>
        <List spacing={3}>
          <ListItem>
            <ListIcon as={FaGlobe} />
            <Link href="https://neatlabs.ai/support" isExternal color="blue.500">
              Support Portal
            </Link>
          </ListItem>
          <ListItem>
            <ListIcon as={FaRocket} />
            <Link href="https://neatlabs.ai/feedback" isExternal color="blue.500">
              Feature Requests
            </Link>
          </ListItem>
        </List>
      </Box>
    </Box>
  );
}

export default HelpSection;